import { useState, useEffect } from 'react';
import { config } from '../config';

interface AuthResponse {
  message: string;
  user?: {
    email: string;
    id: string;
    api_key?: string;
  };
}

interface LogoutResponse extends AuthResponse {
  redirect_url?: string;
}

interface GoogleUser {
  email: string;
  id_token: string;
}

// Add a custom error class if you don't have one
export class AuthError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AuthError';
  }
}

/**
 * Helper function to make authenticated API calls
 * @param endpoint - API endpoint to call
 * @param options - Fetch options
 * @returns Promise with the API response
 */
export const authFetch = async (endpoint: string, options?: RequestInit): Promise<AuthResponse> => {
  console.log(`🔐 Making auth request to ${endpoint}`);

  const response = await fetch(`${config.API_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
    credentials: 'include',
  });

  if (response.status === 401) {
    throw new AuthError('Please login to continue');
  }

  // For logout specifically, we don't try to parse JSON if status is 204
  if (endpoint === '/auth/logout' && response.status === 204) {
    return { message: 'Logout successful' };
  }

  let data;
  try {
    data = await response.json();
  } catch (error) {
    console.error('Failed to parse JSON response:', error);
    throw new Error('Invalid server response');
  }

  if (!response.ok) {
    console.error(`❌ Auth request failed: ${data.message}`);
    throw new Error(data.message || 'Authentication failed');
  }

  console.log(`✅ Auth request successful: ${endpoint}`);
  return data;
};

/**
 * Custom hook for handling authentication state and operations
 *
 * Provides functionality for:
 * - User login
 * - User registration
 * - Logout
 * - Google Sign-In
 * - Managing login modal state
 */
export const useAuth = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Add debug logging for state changes
  useEffect(() => {
    console.log('Auth state changed:', { isLoggedIn, isLoading });
  }, [isLoggedIn, isLoading]);

  // Check auth status on mount
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        await authFetch('/auth/user');
        setIsLoggedIn(true);
      } catch (error) {
        setIsLoggedIn(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  /**
   * Handles user login with email and password
   */
  const handleLogin = async (email: string, password: string) => {
    console.log(`🔑 Attempting login for user: ${email}`);
    try {
      await authFetch('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });
      console.log('✅ Login successful');
      setIsLoggedIn(true);
      setIsLoginModalOpen(false);
    } catch (error) {
      console.error('❌ Login failed:', error);
      throw error;
    }
  };

  /**
   * Handles user registration with email and password
   */
  const handleRegister = async (email: string, password: string) => {
    console.log(`📝 Attempting registration for user: ${email}`);
    try {
      await authFetch('/auth/register', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });
      console.log('✅ Registration successful');
      setIsLoggedIn(true);
      setIsLoginModalOpen(false);
    } catch (error) {
      console.error('❌ Registration failed:', error);
      throw error;
    }
  };

  /**
   * Handles user logout
   */
  const handleLogout = async () => {
    try {
      const response = await authFetch('/auth/logout', {
        method: 'POST',
        credentials: 'include',
      }) as LogoutResponse;

      // Set logged out state before potential redirect
      setIsLoggedIn(false);
      console.log('Auth state changed after logout:', { isLoggedIn: false });

      if (response.redirect_url) {
        // Redirect to Google's logout URL which will handle the complete logout
        window.location.href = response.redirect_url;
      }
    } catch (error) {
      console.error('❌ Logout failed:', error);
      throw error;
    }
  };

  return {
    isLoggedIn,
    isLoginModalOpen,
    isLoading,
    handleLogin,
    handleRegister,
    handleLogout,
    setIsLoginModalOpen,
    setIsLoggedIn
  };
};